import React, { useState } from 'react';
import './AddToCalendarButton.scss';

interface AddToCalendarButtonProps {
  name: string;
  startDate: string;
  endDate: string;
  location?: string;
  description?: string;
  buttonStyle?: 'round' | 'default';
  hideTextLabelButton?: boolean;
  hideTextLabelList?: boolean;
  options?: string[];
}

export const AddToCalendarButton: React.FC<AddToCalendarButtonProps> = ({
  name,
  startDate,
  endDate,
  location = '',
  description = '',
  buttonStyle = 'default',
  hideTextLabelButton = false,
  hideTextLabelList = false,
  options = ['Apple', 'Google', 'Yahoo', 'iCal']
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const generateCalendarLinks = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const formatDate = (date: Date) => {
      return date.toISOString().replace(/-/g, '').replace(/:/g, '').slice(0, 15) + 'Z';
    };

    const startFormatted = formatDate(start);
    const endFormatted = formatDate(end);
    
    const eventDetails = {
      text: name,
      dates: `${startFormatted}/${endFormatted}`,
      details: description,
      location: location
    };

    return {
      google: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventDetails.text)}&dates=${eventDetails.dates}&details=${encodeURIComponent(eventDetails.details)}&location=${encodeURIComponent(eventDetails.location)}`,
      apple: `data:text/calendar;charset=utf8,BEGIN:VCALENDAR%0D%0AVERSION:2.0%0D%0ABEGIN:VEVENT%0D%0AURL:${encodeURIComponent(window.location.href)}%0D%0ADTSTART:${startFormatted}%0D%0ADTEND:${endFormatted}%0D%0ASUMMARY:${encodeURIComponent(eventDetails.text)}%0D%0ADESCRIPTION:${encodeURIComponent(eventDetails.details)}%0D%0ALOCATION:${encodeURIComponent(eventDetails.location)}%0D%0AEND:VEVENT%0D%0AEND:VCALENDAR`,
      yahoo: `https://calendar.yahoo.com/?v=60&view=d&type=20&text=${encodeURIComponent(eventDetails.text)}&st=${startFormatted}&et=${endFormatted}&desc=${encodeURIComponent(eventDetails.details)}&in_loc=${encodeURIComponent(eventDetails.location)}`,
      ical: `data:text/calendar;charset=utf8,BEGIN:VCALENDAR%0D%0AVERSION:2.0%0D%0ABEGIN:VEVENT%0D%0AURL:${encodeURIComponent(window.location.href)}%0D%0ADTSTART:${startFormatted}%0D%0ADTEND:${endFormatted}%0D%0ASUMMARY:${encodeURIComponent(eventDetails.text)}%0D%0ADESCRIPTION:${encodeURIComponent(eventDetails.details)}%0D%0ALOCATION:${encodeURIComponent(eventDetails.location)}%0D%0AEND:VEVENT%0D%0AEND:VCALENDAR`
    };
  };

  const handleCalendarAction = (type: string) => {
    const links = generateCalendarLinks();
    const link = links[type as keyof typeof links];
    
    if (link) {
      if (type === 'apple' || type === 'ical') {
        const linkElement = document.createElement('a');
        linkElement.href = link;
        linkElement.download = `${name.replace(/\s+/g, '_')}.ics`;
        document.body.appendChild(linkElement);
        linkElement.click();
        document.body.removeChild(linkElement);
      } else {
        window.open(link, '_blank');
      }
    }
    
    setIsOpen(false);
  };

  const buttonClass = buttonStyle === 'round' ? 'calendar-button-round' : 'calendar-button-default';

  return (
    <div className="add-to-calendar-container">
      <button
        className={buttonClass}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Add to calendar"
      >
        {!hideTextLabelButton && <span>📅</span>}
        {!hideTextLabelButton && <span>Add to Calendar</span>}
      </button>
      
      {isOpen && (
        <div className="calendar-dropdown">
          {options.map((option) => (
            <button
              key={option}
              className="calendar-option"
              onClick={() => handleCalendarAction(option.toLowerCase())}
            >
              {!hideTextLabelList && option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
